import React, { useRef, useLayoutEffect } from "react";
import { RouteComponentProps, useParams } from "react-router-dom";
import TableOrganism from "../../organisms/Table/TableOrganism";
import { createHeaderRow } from "../../utilities/CreateHeaderRow";
import { useQuery } from "react-query";
import { TableRowProps } from "../../molecules/TableRow/TableRowAtom";
import { createVerifiedItemsRows } from "./VerifiedItemsTable";
import IPage from "../../config/interfaces/page";
import { useLoaderDispatch, useLoaderState } from "../../context/LoaderContext";
import { startLoading, stopLoading } from "../../context/actions/loaderActions";
import Loader from "../../atoms/Loader/LoaderAtom";
import useApiService from "../../utilities/useApiService";
import { useAuthState } from "../../context/AuthContext";
import { mediumButton } from '../../utilities/style';
import { PrintButton } from "../../atoms/PrintButton/PrintButtonAtom";
import { printStyles } from "../../atoms/PrintButton/PrintButtonAtomStyles";
import PrintHeaderInfo from "../../organisms/PrintHeaderInfo/PrintHeaderInfo";
import { UserRole } from "../../utilities/Enums";
import { isRoleIncluded } from "../../utilities/checkUserRole";
import { useStyles } from "./VerifiedItemsStyles";
import { CallToActionButton } from "../../atoms/callToActionButton/CallToActionButton";
import { GenerateReportSelector } from "../../atoms/GenerateReportSelector/GenerateReportSelectorAtom";
import { useInitialReports } from '../../hooks/useInitialReports';
import { formatLocalTime } from "../../utilities/DataMatrixParser/formatTime";
import { formatNumberWithCommas } from "../../utilities/formatNumber";
import AppBreadcrumbs from "../../organisms/Breadcrumbs/Breadcrumbs";

export interface VerifiedItemsProps {}

export const VerifiedItems: React.FC<
  VerifiedItemsProps & RouteComponentProps<any> & IPage
> = (props) => {
  const { user } = useAuthState();
  const classes = useStyles({});
  const userRole = user!.role;
  const { getMethod } = useApiService();
  const { initialReports } = useInitialReports();
  const params: any = useParams();
  const { loading } = useLoaderState();
  const loaderDispatch = useLoaderDispatch();
  const labelRef = useRef<HTMLDivElement>(null);
  const isAdminLikeRole = userRole !== UserRole.User;
  const basePath = isAdminLikeRole ? "/admin/pharmacies" : "/pharmacies";
  
  const headerTitles = [
    "NDC",
    "Manufacturer",
    "Product",
    "PKG Size",
    // "Type",
    "Full Quantity",
    "Partial Quantity",
    "CSC",
    "Estimated Price",
    "Expiration Date",
    "Lot Number",
    "Status",
  ];

  const fetchItems = async () => {
    startLoading(loaderDispatch);
    let response;
    response = await getMethod(`/pharmacies/${params.pharmacyId}/returnrequests/${params.requestId}/items`);
    stopLoading(loaderDispatch);
    return response.data;
  };

  const { data } = useQuery("items", fetchItems);
  const rows: Array<TableRowProps> = data ? createVerifiedItemsRows(data) : [];

   const renderPharmacyInfo = () => {
     if (data && data.length > 0) {
       return (
         <PrintHeaderInfo
           pharmacyName={data[0].pharmacyDoingBusinessAs}
           createdAt={formatLocalTime(data[0].returnRequestCreatedAt)}
         />
       );
     }
     return null;
   };

  const rawTotal = data 
    ? data.reduce((sum: number, item: any) => 
        sum + 
        (item.extendedActualPrice && parseFloat(item.extendedActualPrice) !== 0 ? parseFloat(item.extendedActualPrice) : parseFloat(item.extendedPrice) * 0.7 || 0) + 
        (item.extendedUnitActualPrice && parseFloat(item.extendedUnitActualPrice) !== 0 ? parseFloat(item.extendedUnitActualPrice) : parseFloat(item.extendedUnitPrice) * 0.7 || 0), 
        0
      ) 
    : 0;

  const totalSum = formatNumberWithCommas(rawTotal, true);

const pharmacyName = data?.[0]?.pharmacyDoingBusinessAs || "Pharmacy";

const breadcrumbItems = [
  {
    label: pharmacyName,
    path: `${basePath}/${params.pharmacyId}`,
  },
  {
    label: "Pharmacy Returns",
    path: `${basePath}/${params.pharmacyId}/returns`,
  },
  {
    label: "Items",
  },
];


  return (
    <div>
      {renderPharmacyInfo()}
      <div className={classes.breadcrumbsContainer}>
        <AppBreadcrumbs items={breadcrumbItems} />
      </div>
      {loading ? (
        <Loader />
      ) : (
        <div style={{ marginBottom: 40 }} ref={labelRef}> 
          <h1 className="print-title">Expected Returns</h1>
          <style>{printStyles}</style>
            <TableOrganism
              header={createHeaderRow(headerTitles)}
              rows={rows}
              tableClassName="print-table"
            />
            <h2 className="total">
           Total: {totalSum}
          </h2>
        </div>
      )}
      <div className={classes.buttonContainer}>
        {data?.some((item: any) => item.status === "PENDING") && (
        <GenerateReportSelector
                      buttonText="Generate Initial Report"
                      buttonHeight="80px"
                      buttonWidth={mediumButton.width}
                      availableReports={initialReports}
                      documentType="pdf"
                      hideReports={['RETURN-REPORT', 'SCHEDULE-INITIAL',]}
                   />
        )}
        {isRoleIncluded([UserRole.Admin, UserRole.Warehouse], userRole) && (
          <CallToActionButton
            text="Accept / Reject"
            width ={mediumButton.width}
            onClick={() => {
              props.history.push(
                `/admin/pharmacies/${params.pharmacyId}/returnrequests/${params.requestId}/items`
              );
            }}
          />
        )}
      </div>
    </div>
  );
};
